import { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { groupDatesByMonth } from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/PayrollCalendarDates/hooks/usePayrollCalendarDates';

import { usePayrollCalendarDatesStore } from '../../stores';

const STEP = 6;

export const usePayrollCalendarDates = (editMode?: boolean) => {
  const [page, setPage] = useState(0);
  const dates = usePayrollCalendarDatesStore((state) => state.dates);
  const handleSetDates = usePayrollCalendarDatesStore((state) => state.handleSetDates);
  const initialMonthDate = usePayrollCalendarDatesStore((state) => state.initialMonthDate);
  const minDate = usePayrollCalendarDatesStore((state) => state.minDate);
  const endDate = usePayrollCalendarDatesStore((state) => state.endDate);

  const { monthCount, maxStep, currentMonthPage } = groupDatesByMonth(dates, STEP, initialMonthDate);

  const onPickDate = useCallback(
    (date: Dayjs) => {
      const preparedDate = date.format('YYYY-MM-DD') as string;
      if (dates.includes(preparedDate)) {
        handleSetDates(dates.filter((d) => d !== preparedDate));
      } else {
        const preparedSortedDates = [...dates, preparedDate].sort((a, b) => dayjs(a).diff(dayjs(b)));
        handleSetDates(preparedSortedDates);
      }
    },
    [dates, handleSetDates]
  );

  const onPickDates = useCallback(
    (newDates: Dayjs[]) => {
      const filteredDates = newDates.filter((date) => date.isValid());
      const preparedDates = filteredDates.map((date) => date.format('YYYY-MM-DD') as string);
      const datesToAdd = dates.filter((date) => !preparedDates.includes(date));
      const preparedSortedDates = [...datesToAdd, ...preparedDates].sort((a, b) => dayjs(a).diff(dayjs(b)));
      handleSetDates(preparedSortedDates);
    },
    [dates, handleSetDates]
  );

  useEffect(() => {
    if (currentMonthPage !== 0) {
      setPage(currentMonthPage);
    }
  }, [currentMonthPage]);

  useEffect(() => {
    setPage(editMode ? currentMonthPage : 0);
  }, [initialMonthDate, editMode, currentMonthPage]);

  return {
    dates,
    onPickDate,
    onPickDates,
    page,
    setPage,
    initialMonthDate,
    minDate,
    endDate,
    maxStep,
    monthCount,
  };
};
